import { useEffect, useState } from 'react';
import AdminLayout from '@/components/admin/AdminLayout';
import {
    BookOpen,
    Users,
    ShoppingCart,
    MessageSquare,
    TrendingUp,
    ArrowUpRight,
    Clock
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { supabase } from '@/lib/supabaseClient';
import { safeFetch } from '@/lib/safeFetch';

interface RecentOrder {
    id: string;
    amount: number;
    status: string;
    created_at: string;
    userName: string;
    planName: string;
}

const AdminDashboard = () => {
    const [stats, setStats] = useState({
        courses: 0,
        users: 0,
        orders: 0,
        leads: 0,
        revenue: 0
    });
    const [recentOrders, setRecentOrders] = useState<RecentOrder[]>([]);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const loadDashboard = async () => {
            try {
                const fetchCount = async (table: string) => {
                    const { count, error } = await supabase.from(table).select('*', { count: 'exact', head: true });
                    if (error) console.error(`Error fetching ${table} count:`, error);
                    return count || 0;
                };

                console.log("--- DASHBOARD DEBUG: START FETCH ---");

                // Fetch stats and revenue first
                const [
                    coursesCount,
                    usersCount,
                    ordersCount,
                    leadsCount,
                    revenueResponse
                ] = await Promise.all([
                    fetchCount('courses'),
                    fetchCount('profiles'),
                    fetchCount('payments'),
                    fetchCount('leads'),
                    supabase.from('payments').select('amount').in('status', ['success', 'paid'])
                ]);

                if (revenueResponse.error) console.error("Revenue Fetch Error:", revenueResponse.error);

                // Fetch Recent Transactions Defensively
                // Try joined query first
                let { data: recentData, error: recentError } = await supabase.from('payments')
                    .select('*, courses(title)')
                    .order('created_at', { ascending: false })
                    .limit(5);

                // Fallback if relationship is missing (PGRST200)
                if (recentError && (recentError.code === 'PGRST200' || recentError.message.includes('relationship'))) {
                    console.warn("Foreign Key missing. Synchronizing fallback protocol...");
                    const { data: rawPayments } = await supabase.from('payments')
                        .select('*')
                        .eq('status', 'paid')
                        .order('created_at', { ascending: false })
                        .limit(5);

                    if (rawPayments) {
                        const userIds = [...new Set(rawPayments.map(p => p.user_id))];
                        const planIds = [...new Set(rawPayments.map(p => p.plan_id))];

                        const [usersRes, plansRes] = await Promise.all([
                            supabase.from('profiles').select('id, full_name').in('id', userIds),
                            supabase.from('plans').select('id, name').in('id', planIds)
                        ]);

                        recentData = rawPayments.map(p => ({
                            ...p,
                            profiles: usersRes.data?.find(u => u.id === p.user_id),
                            plans: plansRes.data?.find(pl => pl.id === p.plan_id)
                        }));
                    }
                } else if (recentError) {
                    console.error("Recent Transactions Fetch Error:", recentError);
                }

                const revenueData = revenueResponse.data;
                const totalRevenue = revenueData?.reduce((acc, curr) => acc + (curr.amount || 0), 0) || 0;

                setStats({
                    courses: coursesCount || 0,
                    users: usersCount || 0,
                    orders: ordersCount || 0,
                    leads: leadsCount || 0,
                    revenue: totalRevenue
                });

                const formattedRecent = (recentData || []).map((o: any) => ({
                    id: o.id,
                    amount: o.amount,
                    status: o.status,
                    created_at: o.created_at,
                    userName: o.name || 'Anonymous Student',
                    planName: o.courses?.title || 'Course Access'
                }));
                setRecentOrders(formattedRecent);

            } catch (e) {
                console.error("Dashboard load failed", e);
            } finally {
                setIsLoading(false);
            }
        };

        loadDashboard();
    }, []);

    const cards = [
        { label: 'Total Courses', value: stats.courses, icon: BookOpen, color: 'blue', trend: 'Total Courses' },
        { label: 'Total Users', value: stats.users, icon: Users, color: 'purple', trend: 'Users' },
        { label: 'Completed Orders', value: stats.orders, icon: ShoppingCart, color: 'emerald', trend: 'Orders' },
        { label: 'Total Enquiries', value: stats.leads, icon: MessageSquare, color: 'amber', trend: 'Enquiries' },
    ];

    return (
        <AdminLayout>
            <div className="space-y-10">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                    <div>
                        <h2 className="text-2xl md:text-3xl font-black text-slate-900 uppercase tracking-tighter">Admin Dashboard</h2>
                        <p className="text-slate-500 font-medium text-xs md:text-sm">Summary of your website's activity.</p>
                    </div>
                    <div className="flex items-center gap-3 px-4 md:px-6 py-2 md:py-3 bg-white border border-slate-200 rounded-2xl shadow-sm w-fit">
                        <Clock className="w-4 h-4 md:w-5 md:h-5 text-accent" />
                        <span className="text-[9px] md:text-[10px] font-black uppercase tracking-widest text-slate-900">Live Updates</span>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
                    {cards.map((card, i) => (
                        <div key={i} className="bg-white p-6 md:p-8 rounded-[1.5rem] md:rounded-[2rem] border border-slate-100 shadow-sm hover:shadow-md transition-all group overflow-hidden relative min-w-0">
                            <div className="absolute top-0 right-0 p-8 opacity-5 group-hover:scale-110 transition-transform">
                                <card.icon className="w-20 h-20" />
                            </div>
                            <div className="relative z-10 flex flex-col h-full">
                                <div className={cn(
                                    "w-12 h-12 rounded-2xl flex items-center justify-center mb-6 border shadow-inner",
                                    card.color === 'blue' ? "bg-blue-50 border-blue-100 text-blue-600" :
                                        card.color === 'purple' ? "bg-purple-50 border-purple-100 text-purple-600" :
                                            card.color === 'emerald' ? "bg-emerald-50 border-emerald-100 text-emerald-600" :
                                                "bg-amber-50 border-amber-100 text-amber-600"
                                )}>
                                    <card.icon className="w-6 h-6" />
                                </div>
                                <h3 className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-1">{card.label}</h3>
                                <div className="text-3xl font-black text-slate-900 tracking-tighter mb-4">{card.value}</div>
                                <div className="mt-auto flex items-center gap-2 text-[10px] font-black uppercase tracking-tight text-emerald-600 bg-emerald-50 px-3 py-1.5 rounded-full w-fit">
                                    <ArrowUpRight className="w-3 h-3" />
                                    {card.trend}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    <div className="lg:col-span-3 bg-white rounded-[1.5rem] md:rounded-[2.5rem] border border-slate-100 shadow-sm overflow-hidden flex flex-col">
                        <div className="p-6 md:p-8 border-b border-slate-100 flex items-center justify-between">
                            <h3 className="font-black uppercase tracking-widest text-[10px] md:text-xs text-slate-900">Latest Payments</h3>
                        </div>
                        <div className="flex-1 overflow-x-auto">
                            <table className="w-full min-w-[600px] md:min-w-0">
                                <thead className="bg-slate-50 border-b border-slate-100">
                                    <tr>
                                        <th className="px-8 py-4 text-left text-[9px] font-black uppercase tracking-widest text-slate-400">Order ID</th>
                                        <th className="px-8 py-4 text-left text-[9px] font-black uppercase tracking-widest text-slate-400">Student Name</th>
                                        <th className="px-8 py-4 text-left text-[9px] font-black uppercase tracking-widest text-slate-400">Course Plan</th>
                                        <th className="px-8 py-4 text-left text-[9px] font-black uppercase tracking-widest text-slate-400">Amount</th>
                                        <th className="px-8 py-4 text-left text-[9px] font-black uppercase tracking-widest text-slate-400">Status</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100">
                                    {recentOrders.length > 0 ? recentOrders.map((order) => (
                                        <tr key={order.id} className="hover:bg-slate-50/50 transition-colors">
                                            <td className="px-8 py-6 text-[10px] font-black text-slate-900">{order.id.slice(0, 8)}...</td>
                                            <td className="px-8 py-6">
                                                <div className="flex flex-col">
                                                    <span className="text-[10px] font-black text-slate-900 uppercase tracking-tight">{order.userName}</span>
                                                </div>
                                            </td>
                                            <td className="px-8 py-6 text-[10px] font-bold text-slate-600 uppercase tracking-widest">{order.planName}</td>
                                            <td className="px-8 py-6 text-[10px] font-black text-slate-900">₹{order.amount.toLocaleString()}</td>
                                            <td className="px-8 py-6">
                                                <span className={`px-3 py-1.5 rounded-full text-[9px] font-black uppercase tracking-widest border ${order.status === 'success' || order.status === 'paid'
                                                    ? 'bg-emerald-50 text-emerald-600 border-emerald-100'
                                                    : order.status === 'pending'
                                                        ? 'bg-amber-50 text-amber-600 border-amber-100'
                                                        : order.status === 'failed'
                                                            ? 'bg-red-50 text-red-600 border-red-100'
                                                            : 'bg-slate-50 text-slate-400 border-slate-100'
                                                    }`}>
                                                    {order.status}
                                                </span>
                                            </td>
                                        </tr>
                                    )) : (
                                        <tr>
                                            <td colSpan={5} className="px-8 py-10 text-center text-[10px] font-black uppercase tracking-widest text-slate-400">No active transactions detected</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        </AdminLayout>
    );
};

export default AdminDashboard;
